import React, { useEffect, useState } from "react";
import { useMessageApi } from "../../../provider/AntdNotification";
import TabsContain from "../../../views/components/reuseComp/TabsContain";
import { useQueryState } from "nuqs";
import HeaderSection from "./HeaderSection";
import { Form } from "antd";
import {
  useHomeDataDetail,
  useHomeDataSave,
} from "../../../hooks/api/homepage/hooks";
import { getSingleImageUrl } from "../../../lib/utils";
import HomeBanner from "./HomeBanner";
import AboutUsSection from "./AboutUsSection";
import OurServicesSection from "./OurServicesSection";
import Footer from "./Footer";
import Blog from "./Blog";
import { IHomepagePayload } from "../../../types/api/homepage";
import { IHighlights } from "../../../types/api/service";
import GreenJournal from "./GreenJournal";
import GetInTouch from "../service/detail/GetInTouch";
import PromoBanner from "./PromoBanner";
import ExperienceTotalQuality from "./ExperienceTotalQuality";
import OurTransformation from "./OurTransformation";
import WhyChooseTQP from "./WhyChooseTQP";

const HomeTabs: React.FC<{ setBreadcrumb: (value: string) => void }> = ({
  setBreadcrumb,
}) => {
  const message = useMessageApi();
  const [tab, setTab] = useQueryState("tab");
  const [form] = Form.useForm();
  const activeTab = tab || "Header";
  const [isEditing, setIsEditing] = useState(false);
  const [pageData, setPageData] = useState<any>(null);

  const [reviewMode, setReviewMode] = useState<"list" | "add" | "edit">("list");
  const [editingReviewIndex, setEditingReviewIndex] = useState<number | null>(
    null,
  );

  const { data } = useHomeDataDetail();
  const { mutate: updateHomeData, isPending } = useHomeDataSave({});

  const handleChangeTab = (section: string) => {
    setTab(section);
    setIsEditing(false);
    setBreadcrumb(section);
    setReviewMode("list");
    setEditingReviewIndex(null);
  };

  useEffect(() => {
    if (data?.data) setPageData(data.data);
  }, [data]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      const { createdAt, updatedAt, __v, key, ...rest } = pageData;

      let payload: IHomepagePayload = { ...rest };

      if (activeTab === "Header") {
        payload = {
          ...rest,
          header: {
            ...rest.header,
            ...values.header,
            logo: getSingleImageUrl(values?.header?.logo) || rest?.header?.logo,
          },
        };
      } else if (activeTab === "Banner") {
        payload = {
          ...rest,
          banner: {
            ...rest.banner,
            ...values.banner,
            image:
              getSingleImageUrl(values?.banner?.image) || rest?.banner?.image,
          },
        };
      } else if (activeTab === "About Us") {
        const existingAbout = rest?.aboutus || {};
        const existingHighlights = existingAbout?.highlights || [];
        const newHighlights = values?.aboutus?.highlights || [];

        let updatedHighlights = [...existingHighlights];

        if (reviewMode === "add") {
          updatedHighlights = [...existingHighlights, ...newHighlights];
        }

        if (reviewMode === "edit" && editingReviewIndex !== null) {
          updatedHighlights[editingReviewIndex] = newHighlights[0];
        }

        payload = {
          ...rest,
          aboutus: {
            heading: values?.aboutus?.heading ?? existingAbout?.heading,
            subtext: values?.aboutus?.subtext ?? existingAbout?.subtext,
            image:
              values?.aboutus?.image !== undefined
                ? getSingleImageUrl(values.aboutus.image)
                : existingAbout?.image,
            highlights: updatedHighlights,
          },
        };

        setEditingReviewIndex(null);
        setReviewMode("list");
      } else if (activeTab === "Our Services") {
        /* ---------------- OUR SERVICES ---------------- */
        payload = {
          ...rest,
          our_services: {
            ...rest.our_services,
            ...values.our_services,
          },
        };
      } else if (activeTab === "Why Choose TQP") {
        const existingData = rest?.why_choose_tqp || {};

        const existingTrackRecord = existingData?.trackRecord || [];
        const newTrackRecord = values?.why_choose_tqp?.trackRecord || [];

        let updatedTrackRecord = [...existingTrackRecord];

        if (reviewMode === "add") {
          updatedTrackRecord = [...existingTrackRecord, ...newTrackRecord];
        }

        if (reviewMode === "edit" && editingReviewIndex !== null) {
          updatedTrackRecord[editingReviewIndex] = newTrackRecord[0];
        }

        payload = {
          ...rest,
          why_choose_tqp: {
            heading: values?.why_choose_tqp?.heading ?? existingData?.heading,
            subtext: values?.why_choose_tqp?.subtext ?? existingData?.subtext,
            trackRecord: updatedTrackRecord,
          },
        };

        setEditingReviewIndex(null);
        setReviewMode("list");
      } else if (activeTab === "Promo Banner") {
        /* ---------------- PROMO BANNER ---------------- */
        payload = {
          ...rest,
          promo_banner: {
            ...rest.promo_banner,
            ...values.promo_banner,
            image:
              getSingleImageUrl(values?.promo_banner?.image) ||
              rest?.promo_banner?.image,
          },
        };
      } else if (activeTab === "Experience Total Quality Transformation") {
        payload = {
          ...rest,
          experience_tqp: {
            ...rest.experience_tqp,
            ...values.experience_tqp,
            image: values?.experience_tqp?.image?.length
              ? values.experience_tqp.image.map((img: any) =>
                  getSingleImageUrl(img),
                )
              : rest?.experience_tqp?.image || [],
          },
        };
      } else if (activeTab === "Our Transformation") {
        const existingData = rest?.our_transformation || {};
        const existingTestimonials = existingData?.testimonial || [];
        const newTestimonials = values?.our_transformation?.testimonial || [];

        let updatedTestimonials = [...existingTestimonials];

        if (reviewMode === "add") {
          updatedTestimonials = [...existingTestimonials, ...newTestimonials];
        }

        if (reviewMode === "edit" && editingReviewIndex !== null) {
          updatedTestimonials[editingReviewIndex] = newTestimonials[0];
        }

        payload = {
          ...rest,
          our_transformation: {
            heading:
              values?.our_transformation?.heading ?? existingData?.heading,
            subtext:
              values?.our_transformation?.subtext ?? existingData?.subtext,
            testimonial: updatedTestimonials,
          },
        };

        setEditingReviewIndex(null);
        setReviewMode("list");
      } else if (activeTab === "Get In Touch") {
        /* ---------------- GET IN TOUCH ---------------- */
        payload = {
          ...rest,
          getInTouch: {
            ...rest.getInTouch,
            ...values.getInTouch,
          },
        };
      } else if (activeTab === "Green Journal") {
        payload = {
          ...rest,
          green_journal: {
            ...rest.green_journal,
            ...values.green_journal,
          },
        };
      } else if (activeTab === "Footer") {
        payload = {
          ...rest,
          footer: {
            ...rest.footer,
            ...values.footer,
          },
        };
      }

      updateHomeData(payload, {
        onSuccess: (response: any) => {
          const updatedData = response?.data || payload;

          message.success("Home updated successfully");

          setPageData(updatedData);
          setIsEditing(false);
          setEditingReviewIndex(null);
          setReviewMode("list");
        },
        onError: (error: any) => {
          message.error(
            error?.response?.data?.message || "Failed to save homepage",
          );
        },
      });
    } catch (error) {
      console.log("❌ Validation failed:", error);
    }
  };
  const handleCancel = () => {
    setIsEditing(false);
    setReviewMode("list");
    setEditingReviewIndex(null);
  };

  const sections = [
    "Header",
    "Banner",
    "About Us",
    "Our Services",
    "Why Choose TQP",
    "Promo Banner",
    "Experience Total Quality Transformation",
    "Our Transformation",
    "Get In Touch",
    "Green Journal",
    "Footer",
  ];
  type SectionField = "About Us" | "Our Transformation" | "Why Choose TQP";

  const handleDelete = (index: number, field: SectionField) => {
    if (!pageData) return;

    let payload: IHomepagePayload = { ...pageData };

    if (field === "About Us") {
      const aboutUsData = pageData.aboutus;
      if (!aboutUsData) return;

      const updatedContent = aboutUsData.highlights.filter(
        (_: IHighlights, i: number) => i !== index,
      );

      payload = {
        ...pageData,
        aboutus: { ...aboutUsData, highlights: updatedContent },
      };
    }

    if (field === "Our Transformation") {
      const data = pageData.our_transformation;

      const updatedTestimonials = data?.testimonial?.filter(
        (_: any, i: number) => i !== index,
      );

      payload = {
        ...pageData,
        our_transformation: {
          ...data,
          testimonial: updatedTestimonials,
        },
      };
    }

    if (field === "Why Choose TQP") {
      const data = pageData.why_choose_tqp;

      const updatedTrackRecord = data?.trackRecord?.filter(
        (_: any, i: number) => i !== index,
      );

      payload = {
        ...pageData,
        why_choose_tqp: {
          ...data,
          trackRecord: updatedTrackRecord,
        },
      };
    }

    updateHomeData(payload, {
      onSuccess: () => {
        message.success("Data deleted successfully");
        setPageData(payload);
        setReviewMode("list");
        setEditingReviewIndex(null);
      },
      onError: () => message.error("Failed to delete"),
    });
  };

  return (
    <div>
      <TabsContain
        handleChangeTab={handleChangeTab}
        sections={sections}
        activeTab={activeTab}
      />

      {activeTab === "Header" && (
        <HeaderSection
          handleCancel={handleCancel}
          handleSave={handleSave}
          headerData={pageData?.header || {}}
          form={form}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          loading={isPending}
        />
      )}

      {activeTab === "Banner" && (
        <HomeBanner
          handleCancel={handleCancel}
          handleSave={handleSave}
          bannerData={pageData?.banner || {}}
          form={form}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          loading={isPending}
        />
      )}

      {activeTab === "About Us" && (
        <AboutUsSection
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          handleCancel={handleCancel}
          handleSave={handleSave}
          form={form}
          aboutUsData={pageData?.aboutus}
          loading={isPending}
          onDelete={handleDelete}
          mode={reviewMode}
          setMode={setReviewMode}
          editingIndex={editingReviewIndex}
          setEditingIndex={setEditingReviewIndex}
        />
      )}

      {activeTab === "Our Services" && (
        <OurServicesSection
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          handleCancel={handleCancel}
          handleSave={handleSave}
          form={form}
          ourServiceData={pageData?.our_services}
          loading={isPending}
        />
      )}

      {activeTab === "Our Transformation" && (
        <OurTransformation
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          handleCancel={handleCancel}
          handleSave={handleSave}
          form={form}
          ourTransformationData={pageData?.our_transformation}
          loading={isPending}
          onDelete={handleDelete}
          mode={reviewMode}
          setMode={setReviewMode}
          editingIndex={editingReviewIndex}
          setEditingIndex={setEditingReviewIndex}
        />
      )}

      {activeTab === "Why Choose TQP" && (
        <WhyChooseTQP
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          handleCancel={handleCancel}
          handleSave={handleSave}
          form={form}
          whyChooseTQPData={pageData?.why_choose_tqp}
          loading={isPending}
          onDelete={handleDelete}
          mode={reviewMode}
          setMode={setReviewMode}
          editingIndex={editingReviewIndex}
          setEditingIndex={setEditingReviewIndex}
        />
      )}

      {activeTab === "Promo Banner" && (
        <PromoBanner
          handleCancel={handleCancel}
          handleSave={handleSave}
          bannerData={pageData?.promo_banner}
          form={form}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          loading={isPending}
        />
      )}

      {activeTab === "Experience Total Quality Transformation" && (
        <ExperienceTotalQuality
          handleCancel={handleCancel}
          handleSave={handleSave}
          data={pageData?.experience_tqp}
          form={form}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          loading={isPending}
        />
      )}

      {activeTab === "Get In Touch" && (
        <GetInTouch
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          handleCancel={handleCancel}
          handleSave={handleSave}
          form={form}
          getInTouchData={pageData?.getInTouch}
          loading={isPending}
        />
      )}

      {activeTab === "Green Journal" && (
        <GreenJournal
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          handleCancel={handleCancel}
          handleSave={handleSave}
          form={form}
          greenJournalData={pageData?.green_journal}
          loading={isPending}
        />
      )}
      {activeTab === "Footer" && (
        <Footer
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          handleCancel={handleCancel}
          handleSave={handleSave}
          form={form}
          footerData={pageData?.footer}
          loading={isPending}
        />
      )}
    </div>
  );
};

export default HomeTabs;
